import { useCallback, useState } from 'react';
import { useFormikContext } from 'formik';
import { useSnackbar } from 'notistack';

import { graphql } from '@e-socna/data-layer';

import { useGraphQLMutation } from '../../../hooks/useGraphQLMutation.hook';
import { useMissionReadyContext } from '../../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import { userFriendlyErrors } from '../../../utils/back-errors.util';
import type { BackError } from '../../../utils/back-errors.util';
import type { MissionReportWithItsInfo } from '../../../pages/MissionReport/types/mission-report-with-its-info.type';
import { MessageList } from '../../../components/MessageList';

const MissionReportInfoForm_CodeSuggestion_QueryDocument = graphql(`
  query MissionReportInfoForm_CodeSuggestion_Query(
    $missionId: Id!
    $workflowId: Id!
    $templateId: Id
  ) {
    suggestReportInfoCode(
      missionId: $missionId
      workflowId: $workflowId
      templateId: $templateId
    )
  }
`);

export const useCodeSuggestion = () => {
  const { missionId } = useMissionReadyContext();
  const { enqueueSnackbar } = useSnackbar();

  const { values, setFieldValue } =
    useFormikContext<MissionReportWithItsInfo>();

  const [isLoading, setIsLoading] = useState(false);

  const { mutate: getCodeSuggestionQuery } = useGraphQLMutation(
    MissionReportInfoForm_CodeSuggestion_QueryDocument,
    {
      onMutate: () => {
        setIsLoading(true);
      },
      onSuccess: (data) => {
        void setFieldValue('codingConfig.code', data.suggestReportInfoCode);
      },
      onError: (error: unknown) => {
        enqueueSnackbar(
          <MessageList messages={userFriendlyErrors(error as BackError)} />,
          {
            variant: 'error',
          },
        );
      },
      onSettled: () => {
        setIsLoading(false);
      },
    },
  );

  const codeSuggestionHandler = useCallback(() => {
    getCodeSuggestionQuery({
      missionId,
      workflowId: values.info.workflowId,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      templateId: values.templateId || undefined,
    });
  }, [
    getCodeSuggestionQuery,
    missionId,
    values.info.workflowId,
    values.templateId,
  ]);

  return {
    isLoading,
    codeSuggestionHandler,
  };
};
